import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';

import slugify from '../../support/helpers/slugify';
import { isValidEmail } from '../../core/support/isValidEmail';
import {
  getInitialParams,
  updateInitialParams,
} from '../../support/InitialParams';
import { AccountSignupRuntype } from '../../runtypes/AccountRuntype';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  GridItem,
  Heading,
  HStack,
  Input,
  PasswordInput,
  ScrollPane,
  SimpleGrid,
  useShowMessage,
  VStack,
} from '../../core-ui';

import { useNavigation } from '../Navigation';
import { validateUser } from '../../support/safeValidators';

import AccountEditForm, { Account } from './AccountEditForm';
import {
  CreateAccount,
  CreateAccountVariables,
} from './__generated__/CreateAccount';

const CREATE_ACCOUNT = gql`
  mutation CreateAccount(
    $account: CreateAccountInput!
    $user: CreateAccountUserInput!
    $sendNotification: Boolean!
  ) {
    createAccount(
      account: $account
      user: $user
      sendNotification: $sendNotification
    ) {
      id
    }
  }
`;

const createEmptyAccount = (): Account => {
  return {
    id: '',
    name: '',
    email: '',
    suspended: false,
    contactName: '',
    phoneNumber: '',
    subdomain: '',
    subdomainTitle: '',
    isSystemAdmin: false,
    showNewFeatures: false,
    achOnly: false,
  };
};

function getPrefilledAccountFromUrl(): Account | undefined {
  const queryParams = getInitialParams();
  if (typeof queryParams.newAccount === 'string') {
    const parsed = tryParseJson(queryParams.newAccount);
    const signupInfo = AccountSignupRuntype.guard(parsed) ? parsed : undefined;
    if (!signupInfo) {
      return;
    }
    const { name, marketName, phone, email } = signupInfo;
    return {
      id: '',
      name: marketName,
      email: isValidEmail(email) ? email : '',
      suspended: false,
      contactName: name,
      phoneNumber: phone,
      subdomain: slugify(marketName),
      subdomainTitle: marketName,
      isSystemAdmin: false,
      showNewFeatures: false,
      achOnly: false,
    };
  }
}

export default function AccountNewPage() {
  const { navigate, backTo } = useNavigation();

  const [account] = useState(() => {
    return getPrefilledAccountFromUrl() ?? createEmptyAccount();
  });

  const [username, setUsername] = useState(account.subdomain);
  const [password, setPassword] = useState('');
  const [notificationEnabled, setNotificationEnabled] = useState(true);

  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();

  const [createAccount, { loading: isSaving }] = useMutation<
    CreateAccount,
    CreateAccountVariables
  >(CREATE_ACCOUNT, {
    onError: showApolloError,
    onCompleted: (data) => {
      showMessage('success', t('Account created successfully'));
      const newAccount = data.createAccount;
      navigate('/admin/accounts/:id', { id: newAccount.id });
    },
  });

  return (
    <VStack alignItems="stretch" px={10} pt={10} flex={1} minH={0}>
      <HStack justifyContent="space-between">
        <Heading as="h2" size="lg">
          {t('New Account')}
        </Heading>
        <HStack spacing={2}>
          <Button onClick={() => backTo('/admin/accounts')}>
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            form="account-create"
            colorScheme="blue"
            isLoading={isSaving}
          >
            {t('Save')}
          </Button>
        </HStack>
      </HStack>
      <Divider borderColor="gray.200" />
      <ScrollPane contentContainerProps={{ px: 2, pt: 2, pb: 4 }}>
        <AccountEditForm
          formId="account-create"
          account={account}
          onSave={async ({ id, ...account }) => {
            const user = {
              name: account.contactName,
              email: account.email,
              username,
              password,
            };
            const { isValid, reason } = validateUser(user, {
              allowEmptyPassword: true,
            });
            if (!isValid) {
              showMessage('error', reason);
              return;
            }
            await createAccount({
              variables: {
                account,
                user: user,
                sendNotification: notificationEnabled,
              },
            });
            updateInitialParams((params) => {
              const { newAccount, ...otherParams } = params;
              return otherParams;
            });
          }}
        >
          <SimpleGrid columns={2} gap={2}>
            <GridItem colSpan={1}>
              <FormControl display="flex" alignItems="center" gap={2}>
                <Checkbox
                  isChecked={notificationEnabled}
                  onChange={() => {
                    setNotificationEnabled((checked) => !checked);
                  }}
                />
                <FormLabel m={0}>{t('Send Welcome Email')}</FormLabel>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1} />
            <GridItem colSpan={2}>
              <VStack alignItems="stretch" pt={1}>
                <Heading as="h2" size="lg">
                  {t('Initial User')}
                </Heading>
                <Divider borderColor="gray.200" />
              </VStack>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>{t('Username')}</FormLabel>
                <Input
                  onChange={(event) => setUsername(event.target.value)}
                  value={username}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={1} />
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>{t('Password')}</FormLabel>
                <PasswordInput
                  placeholder={t('Leave blank to auto-generate')}
                  onChange={(event) => setPassword(event.target.value)}
                  value={password}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={1} />
          </SimpleGrid>
        </AccountEditForm>
      </ScrollPane>
    </VStack>
  );
}

function tryParseJson(input: string) {
  try {
    return JSON.parse(input);
  } catch (error) {
    return null;
  }
}
